import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { memo } from 'react';
import toast from 'react-hot-toast';
import { useUsersStore } from '../../store/useUsersStore';
import DeleteModal from '../../components/DeleteModal';


const ROW_PREVIEW_LEN = 220;


const TemplatesListTable = ({ onEdit = () => {}, onUse = () => {} }) => {
  const selectedUser = useUsersStore((s) => s.getSelectedUser());
  const users = useUsersStore((s) => s.users) || [];
  const selectedKey = useUsersStore((s) => s.selectedKey);
  const parentUid = useUsersStore((s) => s.parentUid || s.currentUser || null);
  const aiTemplatesSnapshot = useUsersStore((s) => s.aiTemplates) || [];
  const aiLoading = useUsersStore((s) => s.aiLoading) || false;
  const aiTemplatesMessage = useUsersStore((s) => s.aiTemplatesMessage) || null;
  // const skills = selectedUser?.skills || [];


  const actionsRef = useRef({});
  useEffect(() => {
    const st = useUsersStore.getState();
    actionsRef.current.fetchUsers = st.fetchUsers || null;
    actionsRef.current.loadAiTemplates = st.loadAiTemplates || null;
    actionsRef.current.deleteAiTemplate = st.deleteAiTemplate || st.deleteTemplate || null;
  }, []);


  const getTemplateContent = useCallback((t) => {
    const c = t?.content ?? t?.body ?? t?.text ?? '';
    if (typeof c === 'string') return c;
    if (Array.isArray(c)) return c.join('\n');
    if (c && typeof c === 'object') {
      if (typeof c.text === 'string') return c.text;
      if (typeof c.content === 'string') return c.content;
      try { return JSON.stringify(c); } catch { return String(c); }
    }
    return String(c || '');
  }, []);


  const selectedSubUser = useMemo(() => {
    if (!selectedKey || !Array.isArray(users) || users.length === 0) return null;
    const sid = String(selectedKey);
    return users.find((u) => {
      const idCandidates = [
        u.document_id, u.documentId, u.sub_user_id, u.subUserId, u.id, u._id,
        u.sub_username, u.subUsername, u.username, u.email,
      ];
      return idCandidates.some((c) => typeof c !== 'undefined' && c !== null && String(c) === sid);
    }) || null;
  }, [users, selectedKey]);

    const skills = useMemo(() => {
    const raw = selectedUser?.skills ?? [];
    let arr = [];
    if (Array.isArray(raw)) arr = raw;
    else if (raw && typeof raw === 'object') arr = Object.values(raw);
    else arr = [];

    return arr
      .map((s, i) => {
        if (s === null || typeof s === 'undefined') return null;
        if (typeof s === 'string') return { id: s, name: s };
        if (typeof s === 'object') {
          return { id: s.id ?? s.name ?? `skill-${i}`, name: s.name ?? s.skill ?? String(s) };
        }
        return { id: `skill-${i}`, name: String(s) };
      })
      .filter(Boolean);
  }, [selectedUser]);


  const selectedSubUserId = useMemo(() => {
    if (!selectedSubUser) return null;
    return selectedSubUser.document_id || selectedSubUser.sub_user_id || selectedSubUser.id || null;
  }, [selectedSubUser]);


  const [localTemplates, setLocalTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  
  // Delete Modal State
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);


  const mountedRef = useRef(true);
  useEffect(() => { return () => { mountedRef.current = false; }; }, []);


  useEffect(() => {
    (async () => {
      try {
        if ((Array.isArray(users) && users.length > 0) || !parentUid) return;
        const fetchFn = actionsRef.current.fetchUsers;
        if (typeof fetchFn === 'function') {
          await fetchFn(parentUid);
        }
      } catch (err) {
        console.warn('fetchUsers failed', err);
      }
    })();
  }, [users.length, parentUid]);


  // Sync local list from store when sub-user or store data changes. No API call here – page (AITemplatesPage) is the single caller.
  useEffect(() => {
    if (!selectedSubUserId) {
      setLocalTemplates([]);
      setLoading(false);
      return;
    }
    if (selectedSubUser && Array.isArray(selectedSubUser.ai_templates) && selectedSubUser.ai_templates.length > 0) {
      setLocalTemplates(selectedSubUser.ai_templates);
    } else if (Array.isArray(aiTemplatesSnapshot) && aiTemplatesSnapshot.length > 0) {
      setLocalTemplates(aiTemplatesSnapshot);
    } else {
      setLocalTemplates(aiTemplatesSnapshot || []);
    }
  }, [selectedSubUserId, selectedSubUser, aiTemplatesSnapshot]);


  // Sync local list from store when store updates (e.g. after GET ai-templates returns)
  useEffect(() => {
    if (!Array.isArray(aiTemplatesSnapshot)) return;
    const cur = JSON.stringify(localTemplates || []);
    const snap = JSON.stringify(aiTemplatesSnapshot);
    if (cur !== snap && mountedRef.current) setLocalTemplates(aiTemplatesSnapshot);
  }, [aiTemplatesSnapshot]);

  // Open delete modal
  const openDeleteModal = useCallback((tpl) => {
    setTemplateToDelete(tpl);
    setDeleteModalOpen(true);
  }, []);


  // Close delete modal
  const closeDeleteModal = useCallback(() => {
    setDeleteModalOpen(false);
    setTemplateToDelete(null);
  }, []);


  // Confirm delete
  const confirmDelete = useCallback(async () => {
    if (!templateToDelete || !templateToDelete.id) return;
    
    try {
      const delFn = actionsRef.current.deleteAiTemplate;
      if (typeof delFn === 'function' && selectedSubUserId) {
        await delFn(selectedSubUserId, templateToDelete.id);
      } else if (typeof delFn === 'function') {
        await delFn(templateToDelete.id);
      } else {
        throw new Error('No delete function available');
      }
      
      setLocalTemplates((prev) => 
        Array.isArray(prev) ? prev.filter((p) => p.id !== templateToDelete.id) : []
      );
      
      toast.success('Template deleted');
      closeDeleteModal();
    } catch (err) {
      console.error(err);
      toast.error(err?.message || 'Failed to delete template');
    }
  }, [templateToDelete, selectedSubUserId, closeDeleteModal]);


  const parseLinks = useCallback((raw) => {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw.map(String).map(s => s.trim()).filter(Boolean);
    return String(raw).split(',').map(s => s.trim()).filter(Boolean);
  }, []);


  return (
    <>
      <div className="overflow-x-auto rounded-lg border border-primary-black-shade-10">
        <table className="min-w-full">
          <thead className="bg-primary-blue">
            <tr>
              <th className="px-6 py-3 text-left text-sm font-medium text-white">Content</th>
              <th className="px-6 py-3 text-left text-sm font-medium text-white">Portfolio</th>
              <th className="px-6 py-3 text-left text-sm font-medium text-white">Skills</th>
              <th className="px-6 py-3 text-center text-sm font-medium text-white">Action</th>
            </tr>
          </thead>


          <tbody className="bg-white divide-y divide-primary-black-shade-10">
            {(localTemplates || []).map((t, idx) => {
              const content = getTemplateContent(t);
              const short = content.length > ROW_PREVIEW_LEN ? content.slice(0, ROW_PREVIEW_LEN) + '…' : content;
              const expanded = expandedId === t.id;
              const links = parseLinks(t.portfolioLinks || t.portfolio_links || t.links);
              
              return (
                <tr key={t.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm text-gray-900 max-w-md">
                    <div className="whitespace-pre-wrap break-words">
                      {expanded ? content : short}
                    </div>
                    {content.length > ROW_PREVIEW_LEN && (
                      <button
                        type="button"
                        className="text-xs text-primary-blue mt-2 hover:underline"
                        onClick={() => setExpandedId(expanded ? null : t.id)}
                      >
                        {expanded ? 'Show less' : 'Show more'}
                      </button>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {links.length > 0 ? (
                      <div className="flex flex-col gap-1">
                        {links.map((lnk, i) => (
                          <a 
                            key={i} 
                            href={lnk.startsWith('http') ? lnk : `https://${lnk}`} 
                            target="_blank" 
                            rel="noreferrer" 
                            className="text-primary-blue hover:underline break-all"
                          >
                            {lnk}
                          </a>
                        ))}
                      </div>
                    ) : (
                      <span className="text-gray-500">—</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900 min-w-[120px]">
                    {Array.isArray(t.skills) && t.skills.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {t.skills.slice(0, 3).map((s, i) => (
                          <span key={i} className="text-xs bg-blue-50 text-primary-blue px-2 py-1 rounded border border-blue-200">
                            {s.name || s}
                          </span>
                        ))}
                        {t.skills.length > 3 && (
                          <span className="text-xs text-gray-500">+{t.skills.length - 3} more</span>
                        )}
                      </div>
                    ) : (
                      <span className="text-green-600 text-xs bg-green-50 px-3 py-3 w-20 rounded-xl">All Skills</span>
                    )}
                  </td>
                  <td className="px-2 py-4">
                    <div className="flex items-center justify-center gap-2">
                      {/* Edit Icon */}
                      <button
                        onClick={() => onEdit(t)}
                        className="hover:opacity-70 transition-opacity"
                        title="Edit"
                      >
                        <img 
                          src="/Icons/edit-icon.svg" 
                          alt="Edit" 
                          className="w-8 h-8"
                        />
                      </button>


                      {/* Delete Icon - Now opens modal */}
                      <button
                        onClick={() => openDeleteModal(t)}
                        className="hover:opacity-70 transition-opacity"
                        title="Delete"
                      >
                        <img 
                          src="/Icons/delete-icon.svg" 
                          alt="Delete" 
                          className="w-8 h-8"
                        />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {(localTemplates || []).length === 0 && (
              <tr>
                <td colSpan={4} className="px-6 py-8 text-center text-sm text-gray-500">
                  {aiLoading
                    ? 'Loading...'
                    : (aiTemplatesMessage || 'No templates found.')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>


      {/* Skill Stats */}
      {selectedUser && Array.isArray(skills) && skills.length > 0 && (
        <section className="mt-8 bg-white rounded shadow p-6">
          <h2 className="text-lg font-semibold mb-4">Skill Stats</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4">
            {skills.map((skill) => (
              <div
                key={skill.id ?? skill.name}
                className="border rounded-lg px-4 py-3 shadow-sm bg-light-blue flex flex-col justify-center items-center border-primary-blue"
              >
                <div className="text-sm font-semibold text-blue-700">
                  {skill.name}
                </div>
              </div>
            ))}
          </div>  
        </section>
      )}


      {/* Delete Modal */}
      <DeleteModal
        isOpen={deleteModalOpen}
        onClose={closeDeleteModal}
        onConfirm={confirmDelete}
        templateName="this template"
      />
    </>
  );
};


TemplatesListTable.propTypes = {
  onEdit: PropTypes.func,
  onUse: PropTypes.func,
};


export default memo(TemplatesListTable);
